import React, { useState, useEffect } from "react";

import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";

import { esES } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { SocketContext, SocketManager } from './context/Socket/SocketContext';
import { WebInfoProvider, useWebInfo } from './context/WebInfoContext';
import logger from "./utils/logger";

import Routes from "./routes";
import ErrorBoundary from "./components/ErrorBoundary";
import agendaTheme from "./theme/agendaTheme";
// import DragDropTest from "./components/DragDropTest";

const queryClient = new QueryClient();

const App = () => {
    return (
        <ErrorBoundary>
            <WebInfoProvider>
                <AppContent />
            </WebInfoProvider>
        </ErrorBoundary>
    );
};

// ✅ Componente interno que usa useWebInfo (debe estar dentro del provider)
const AppContent = () => {
    const [locale, setLocale] = useState();

    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const preferredTheme = window.localStorage.getItem("preferredTheme");
    const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");

    // ✅ Obtener información web desde DB (ahora desde contexto compartido)
    const { webInfo } = useWebInfo();

    // Log de prueba al cargar la aplicación
    useEffect(() => {
        logger.debug("🎯 App.js cargado - Aplicación iniciada");
        logger.whatsapp.debug("🧪 LOG DE PRUEBA - WhatsApp desde App.js");
        logger.socket.debug("🧪 LOG DE PRUEBA - Socket desde App.js");
    }, []);

    // ✅ Actualizar título cuando se carguen los datos desde DB (useWebInfo ya actualiza el título)
    // ✅ CORREGIDO: Usar websiteTitle directamente, no appName + appSubtitle
    useEffect(() => {
        if (webInfo?.title && document.documentElement.dataset.storefront !== "true" && !window.location.pathname.startsWith('/tienda/')) {
            document.title = webInfo.title;
            const titleElement = document.getElementById('app-title');
            if (titleElement) {
                titleElement.textContent = webInfo.title;
            }
        }
    }, [webInfo]);

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            },
        }),
        []
    );

    const theme = createTheme(
        {
            scrollbarStyles: {
                "&::-webkit-scrollbar": {
                    width: '8px',
                    height: '8px',
					borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
                    backgroundColor: "#1e3a8a",
					borderRadius: "8px",
                },
            },
            scrollbarStylesSoft: {
                "&::-webkit-scrollbar": {
                    width: "8px",
					borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: mode === "light" ? "#F3F4F6" : "#374151",
					borderRadius: "8px",
                },
            },
            hiddenScrollbar: {
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": {
                    display: "none",
                    height: 0,
                    width: 0,
                },
            },
            palette: {
                type: mode,
                primary: { main: mode === "light" ? "#4F46F5" : "#818cf8", dark: "#4338ca", light: "#eef0ff" },
                secondary: { main: "#e11d48", dark: "#be123c", light: "#ffe4e6" },
                success: { main: "#059669" },
                warning: { main: "#d97706" },
                error: { main: "#dc2626" },
				sair: { main: mode === "light" ? "#4F46F5" : "#374151" },
				vcard: { main: mode === "light" ? "#4F46F5" : "#6b7280" },
                textPrimary: mode === "light" ? "#1f2937" : "#f9fafb",
                borderPrimary: mode === "light" ? "#4F46F5" : "#818cf8",
                dark: { main: mode === "light" ? "#374151" : "#f3f4f6" },
                light: { main: mode === "light" ? "#f9fafb" : "#374151" },
                tabHeaderBackground: mode === "light" ? "#ffffff" : "#1f2937",
                optionsBackground: mode === "light" ? "#ffffff" : "#1f2937",
				options: mode === "light" ? "#ffffff" : "#374151",
				fontecor: mode === "light" ? "#4F46F5" : "#818cf8",
                fancyBackground: mode === "light" ? "#f4f6fb" : "#0f172a",
				bordabox: mode === "light" ? "#e5e7eb" : "#374151",
				newmessagebox: mode === "light" ? "#f3f4f6" : "#374151",
				inputdigita: mode === "light" ? "#ffffff" : "#374151",
				contactdrawer: mode === "light" ? "#ffffff" : "#374151",
				announcements: mode === "light" ? "#f3f4f6" : "#374151",
				login: mode === "light" ? "#ffffff" : "#111827",
				announcementspopover: mode === "light" ? "#ffffff" : "#374151",
				chatlist: mode === "light" ? "#f3f4f6" : "#374151",
				boxlist: mode === "light" ? "#f3f4f6" : "#374151",
				boxchatlist: mode === "light" ? "#f3f4f6" : "#1f2937",
                total: mode === "light" ? "#ffffff" : "#111827",
                messageIcons: mode === "light" ? "#6b7280" : "#d1d5db",
                inputBackground: mode === "light" ? "#ffffff" : "#374151",
                barraSuperior: mode === "light" ? "#4F46F5" : "#1f2937",
				boxticket: mode === "light" ? "#f3f4f6" : "#374151",
				campaigntab: mode === "light" ? "#f3f4f6" : "#374151",
				mediainput: mode === "light" ? "#f3f4f6" : "#111827",
				messageBackground: mode === "light" ? "#efeae2" : "#0b141a",
				messageLeftBg: mode === "light" ? "#ffffff" : "#1f2937",
				messageLeftText: mode === "light" ? "#111b21" : "#f3f4f6",
				messageRightBg: mode === "light" ? "#d9fdd3" : "#005c4b",
				messageRightText: mode === "light" ? "#111b21" : "#e9ffef",
				quotedLeftBg: mode === "light" ? "#f0f2f5" : "#111827",
				quotedRightBg: mode === "light" ? "#cde9c7" : "#115e59",
				quotedSideLeft: mode === "light" ? "#53bdeb" : "#60a5fa",
				quotedSideRight: mode === "light" ? "#06cf9c" : "#2dd4bf",
				dailyTimestampBg: mode === "light" ? "#ffffff" : "#1f2937",
				dailyTimestampText: mode === "light" ? "#54656f" : "#9ca3af",
				messageContactNameColor: mode === "light" ? "#1d4ed8" : "#93c5fd",
				messageDeletedText: mode === "light" ? "rgba(17, 24, 39, 0.45)" : "rgba(243, 244, 246, 0.5)",
				forwardMessageColor: mode === "light" ? "#667781" : "#9ca3af",
            },
            typography: {
                fontFamily: "'Inter', 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                fontSize: 14,
                body1: {
                    fontSize: 14,
                    lineHeight: 1.45,
                },
                body2: {
                    fontSize: 13,
                    lineHeight: 1.4,
                },
                caption: {
                    fontSize: 12,
                    lineHeight: 1.35,
                },
                h5: {
                    fontWeight: 800,
                    letterSpacing: 0,
                },
                button: {
                    fontWeight: 700,
                    letterSpacing: 0,
                    textTransform: "none",
                },
            },
            shape: {
                borderRadius: 12,
            },
            props: {
                MuiButton: {
                    disableElevation: true,
                },
                MuiTextField: {
                    variant: "outlined",
                },
                MuiTooltip: {
                    arrow: true,
                },
            },
            overrides: {
                MuiDialog: {
                    paper: {
                        borderRadius: 12,
                        "@media (max-width: 959.95px)": {
                            margin: 0,
                            width: "100%",
                            maxWidth: "100%",
                            // Muchos modales fijan minWidth en px con makeStyles; en móvil no debe desbordar.
                            minWidth: "0 !important",
                            overflowX: "hidden",
                            maxHeight: "min(92dvh, 100%)",
                            borderRadius: "16px 16px 0 0",
                        },
                    },
                    paperFullScreen: {
                        borderRadius: 0,
                        margin: 0,
                        maxHeight: "100%",
                        width: "100%",
                    },
                    scrollPaper: {
                        "@media (max-width: 959.95px)": {
                            alignItems: "flex-end",
                        },
                    },
                },
                MuiDialogTitle: {
                    root: {
                        "@media (max-width: 959.95px)": {
                            padding: "14px 16px",
                        },
                    },
                },
                MuiDialogContent: {
                    root: {
                        "@media (max-width: 959.95px)": {
                            padding: "8px 16px 16px",
                        },
                    },
                },
                MuiDialogActions: {
                    root: {
                        "@media (max-width: 959.95px)": {
                            padding: "8px 12px calc(8px + env(safe-area-inset-bottom, 0px))",
                            flexWrap: "wrap",
                            gap: 8,
                            "& > :not(:first-child)": { marginLeft: 0 },
                        },
                    },
                },
                MuiButton: {
                    root: {
                        borderRadius: 12,
                        minHeight: 38,
                        padding: "9px 16px",
                        position: "relative",
                        overflow: "hidden",
                        transition: "background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease, filter 180ms ease",
                        "&:hover": {
                            transform: "translateY(-2px)",
                            filter: "saturate(1.08)",
                        },
                        "&:active": {
                            transform: "translateY(0)",
                        },
                        "@media (hover: none)": {
                            "&:hover": {
                                transform: "none",
                                filter: "none",
                            },
                        },
                        "@media (max-width: 959.95px)": {
                            minHeight: 44,
                            padding: "10px 16px",
                        },
                        "& .MuiButton-startIcon": {
                            transition: "transform 180ms ease",
                        },
                        "&:hover .MuiButton-startIcon": {
                            transform: "translateX(-1px) scale(1.08)",
                        },
                    },
                    containedPrimary: {
                        background: mode === "light" ? "#2563eb" : "#3b82f6",
                        boxShadow: "0 10px 22px rgba(37, 99, 235, 0.22)",
                        "&:hover": {
                            background: mode === "light" ? "#1d4ed8" : "#2563eb",
                            boxShadow: "0 14px 30px rgba(37, 99, 235, 0.30)",
                        },
                    },
                    containedSecondary: {
                        background: "#e11d48",
                        color: "#ffffff",
                        boxShadow: "0 10px 22px rgba(225, 29, 72, 0.24)",
                        "&:hover": {
                            background: "#be123c",
                            boxShadow: "0 14px 30px rgba(225, 29, 72, 0.32)",
                        },
                    },
                    outlined: {
                        borderColor: mode === "light" ? "#cbd5e1" : "#475569",
                        borderRadius: 12,
                        backgroundColor: mode === "light" ? "rgba(255,255,255,0.72)" : "rgba(15,23,42,0.72)",
                    },
                    text: {
                        "&:hover": {
                            backgroundColor: mode === "light" ? "#eff6ff" : "rgba(96, 165, 250, 0.12)",
                        },
                    },
                },
                MuiIconButton: {
                    root: {
                        borderRadius: 12,
                        transition: "background-color 180ms ease, color 180ms ease, transform 180ms ease, box-shadow 180ms ease",
                        "&:hover": {
                            transform: "translateY(-2px) scale(1.03)",
                            boxShadow: mode === "light" ? "0 8px 18px rgba(15, 23, 42, 0.10)" : "0 8px 18px rgba(0, 0, 0, 0.25)",
                        },
                        "@media (hover: none)": {
                            "&:hover": {
                                transform: "none",
                                boxShadow: "none",
                            },
                        },
                    },
                    sizeSmall: {
                        padding: 7,
                    },
                },
                MuiPaper: {
                    rounded: {
                        borderRadius: 14,
                    },
                    elevation1: {
                        boxShadow: mode === "light"
                            ? "0 1px 2px rgba(15, 23, 42, 0.06), 0 14px 34px rgba(37, 99, 235, 0.08)"
                            : "0 1px 2px rgba(0, 0, 0, 0.22), 0 8px 24px rgba(0, 0, 0, 0.28)",
                    },
                },
                MuiTableCell: {
                    root: {
                        borderBottom: `1px solid ${mode === "light" ? "#e5e7eb" : "#334155"}`,
                        fontSize: "0.875rem",
                        padding: "12px 16px",
                        "@media (max-width: 959.95px)": {
                            padding: "10px 12px",
                            fontSize: "0.8125rem",
                        },
                    },
                    head: {
                        backgroundColor: mode === "light" ? "#f8fafc" : "#1f2937",
                        color: mode === "light" ? "#334155" : "#e5e7eb",
                        fontSize: "0.75rem",
                        fontWeight: 800,
                        letterSpacing: "0.02em",
                        textTransform: "uppercase",
                        borderTop: `1px solid ${mode === "light" ? "#ffffff" : "#334155"}`,
                    },
                    sizeSmall: {
                        padding: "10px 14px",
                    },
                },
                MuiTableRow: {
                    root: {
                        transition: "background-color 140ms ease",
                        "&$hover:hover": {
                            backgroundColor: mode === "light" ? "#eef6ff" : "#263244",
                        },
                    },
                },
                MuiTableContainer: {
                    root: {
                        borderRadius: 10,
                    },
                },
                MuiChip: {
                    root: {
                        borderRadius: 999,
                        fontWeight: 700,
                    },
                    sizeSmall: {
                        height: 24,
                        fontSize: "0.72rem",
                    },
                },
                MuiOutlinedInput: {
                    root: {
                        borderRadius: 8,
                        backgroundColor: mode === "light" ? "#ffffff" : "#111827",
                        transition: "box-shadow 180ms ease, border-color 180ms ease, background-color 180ms ease",
                        "&:hover $notchedOutline": {
                            borderColor: mode === "light" ? "#94a3b8" : "#64748b",
                        },
                        "&$focused $notchedOutline": {
                            borderColor: mode === "light" ? "#2563eb" : "#60a5fa",
                            borderWidth: 1,
                            boxShadow: mode === "light" ? "0 0 0 4px rgba(37, 99, 235, 0.10)" : "0 0 0 4px rgba(96, 165, 250, 0.12)",
                        },
                    },
                    notchedOutline: {
                        borderColor: mode === "light" ? "#d1d5db" : "#475569",
                    },
                },
                MuiTooltip: {
                    tooltip: {
                        borderRadius: 6,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                    },
                },
                MuiTablePagination: {
                    toolbar: {
                        minHeight: 48,
                    },
                    caption: {
                        color: mode === "light" ? "#475569" : "#cbd5e1",
                        fontWeight: 600,
                    },
                },
            },
            mode,
            // ✅ FASE 0: Módulo Agenda — paleta, tipografía, variables (0257)
            agenda: agendaTheme,
        },
        locale
    );

    useEffect(() => {
        const i18nlocale = localStorage.getItem("i18nextLng");
        const browserLocale =
            i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

        if (browserLocale === "esES") {
            setLocale(esES);
        }
    }, []);

    useEffect(() => {
        window.localStorage.setItem("preferredTheme", mode);
    }, [mode]);



    return (
        <ColorModeContext.Provider value={{ colorMode }}>
            <ThemeProvider theme={theme}>
                <QueryClientProvider client={queryClient}>
                  <SocketContext.Provider value={SocketManager}>
                      <Routes />
                      {/* <DragDropTest /> */}
                  </SocketContext.Provider>
                </QueryClientProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default App;
